import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { chatQuickPrompts } from '@/mocks/ai';
import { getDemoReply, generateId } from '@/lib/ai';
import type { ChatMessage } from '@/lib/ai';

function ChatPanel() {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const firstName = profile?.full_name?.split(' ')[0] || '';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = (text: string) => {
    const clean = text.trim();
    if (!clean || typing) return;

    const userMsg: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: clean,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTyping(true);

    const reply = getDemoReply(clean);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: generateId(), role: 'assistant', content: reply, timestamp: new Date().toISOString() },
      ]);
      setTyping(false);
    }, 900);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="flex flex-col h-[560px]">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-brain-line text-xl text-primary-400" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground-50">
              Assistant IA
            </h3>
            <p className="text-xs text-foreground-400">
              {user ? `Bonjour ${firstName || 'cher membre'} !` : 'Connectez-vous pour discuter'}
            </p>
          </div>
        </div>
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
          Mode démo
        </span>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 mb-4 space-y-3 rounded-xl bg-background-50 border border-background-200/70 p-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
              <i className="ri-sparkling-line text-2xl text-primary-400" />
            </div>
            <p className="font-heading font-semibold text-foreground-50 mb-2">
              Comment puis-je vous aider ?
            </p>
            <p className="text-sm text-foreground-400 mb-4 max-w-sm">
              Posez une question, demandez une rédaction, une traduction ou du code.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {chatQuickPrompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => sendMessage(p)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 border border-background-200/70 text-foreground-50 hover:border-primary-400/40 transition-colors whitespace-nowrap"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-50 border border-background-200/70'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))
        )}
        {typing && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-foreground-500/60 animate-bounce" />
              <span className="w-2 h-2 rounded-full bg-foreground-500/60 animate-bounce [animation-delay:0.15s]" />
              <span className="w-2 h-2 rounded-full bg-foreground-500/60 animate-bounce [animation-delay:0.3s]" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Écrivez votre message…"
          disabled={!user}
          className="flex-1 min-w-0 px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!user || !input.trim() || typing}
          className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-950 hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-send-plane-2-fill text-lg" />
        </button>
      </form>
    </div>
  );
}

export default ChatPanel;